import React, { useState } from 'react';

import {
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { useTranslation } from 'react-i18next';

import {
  Controller, FormProvider, SubmitHandler, useForm,
} from 'react-hook-form';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { useMutation, useQuery } from '@apollo/client';

import { useParams } from 'react-router-dom';

import { getPlatform } from '../../../Onboarding/utils/platformHelpers';

import {
  QRCode,
  QRCodeWrapper,
  StoreImgWrapper,
  QRCodeContainer,
  TextWrapper,
  FormContainer,
  CustomLink,
} from './ReadyToStartSleepioWellness.styles';

import { SleepioWellnessOnboardingParams } from '../../SleepioWellnessOnboardingPage';

import Button from '@/components/Button';

import EmoCloudSleep from '@/assets/EmoCloudSleep.svg';
import LimbotHappy from '@/assets/LimbotHappy.svg';
import AppStore from '@/assets/AppStore.svg';
import GooglePlay from '@/assets/GooglePlay.svg';
import { Platform, isSpark as isSparkHelper } from '@/utils/productHelpers';
import {
  SLEEPIO_APP_STORE_URL,
  SLEEPIO_GOOGLE_PLAY_URL,
  SPARK_APP_STORE_URL,
  SPARK_GOOGLE_PLAY_URL,
} from '@/pages/Onboarding/utils/constants';
import sparkTheme from '@/themes/sparkTheme';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import { PHONE_VALIDATION_REGEX } from '@/utils/constants';
import { SEND_READY_TO_DOWNLOAD_SMS } from '@/apollo/mutations';
import { ME_QUERY } from '@/apollo/queries';
import { SLEEPIO_PROGRAM } from '@/utils/posthog';
import { useUserUid } from '@/hooks/useUserUid';
import { useIsUSUser } from '@/hooks/useIsUSUser';

type Props = {
  authToken: string;
};

export const ReadyToStartSleepioWellness: React.FC<Props> = ({ authToken }) => {
  const posthog = usePostHog();
  const { userUid } = useUserUid();
  const { t } = useTranslation();
  const platform = getPlatform();
  const [phoneNumberSubmitted, setPhoneNumberSubmitted] = useState(false);
  const [textConsentAcknowledgedAt, setTextConsentAcknowledgedAt] = useState('');
  const [submitError, setSubmitError] = useState<string>('');
  const { careTeamUid, product } = useParams<SleepioWellnessOnboardingParams>();
  const [sendReadyToDownloadSms, { loading }] = useMutation(SEND_READY_TO_DOWNLOAD_SMS);
  const isSpark = isSparkHelper(product);
  // SD-1823: Timezone-based detection (Intl.DateTimeFormat) — sync, no API dependency, no latency.
  // Hides SMS form for non-US Sleepio users to avoid Twilio 21408 errors on international numbers.
  const isUSUser = useIsUSUser();
  const showSmsForm = isUSUser || isSpark;
  const schema = z.object({
    phoneNumber: z.string().nonempty({ message: t('coverage.sleepio.patient.phone_number_is_required') })
      .regex(PHONE_VALIDATION_REGEX, { message: t('coverage.sleepio.patient.please_enter_valid_phone_number') }),
    agreeToReceiveTextMessages: z.boolean().refine((value) => value === true),
    marketingConsent: z.boolean().optional(),
  });

  type SetupForm = z.infer<typeof schema>;

  const methods = useForm<SetupForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      phoneNumber: '',
      agreeToReceiveTextMessages: false,
      marketingConsent: false,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit, control, formState: { isValid, errors }, setValue,
  } = methods;

  useQuery(ME_QUERY, {
    skip: !authToken,
    fetchPolicy: 'network-only',
    context: authToken ? { token: authToken } : undefined,
    onCompleted: (data) => {
      const serverPhone = data?.me?.phone ?? '';
      if (serverPhone) {
        setValue('phoneNumber', serverPhone, { shouldValidate: true, shouldDirty: false });
      }
    },
    onError: () => {
      if (isSpark) {
        setSubmitError(t('coverage.errors.generic_error'));
      }
    },
  });

  let storeLink;
  let qrCode;
  const getSleepioTitleKey = () => {
    if (showSmsForm) { return 'coverage.sleepio.patient.download_sleepio_title'; }
    if (platform === Platform.Web) { return 'coverage.sleepio.patient.download_sleepio_non_us_title_desktop'; }
    return 'coverage.sleepio.patient.download_sleepio_non_us_title_mobile';
  };
  const title = isSpark
    ? t('coverage.spark.patient.download_spark_title')
    : t(getSleepioTitleKey());
  const getSleepioSubtitleKey = () => {
    if (showSmsForm) { return 'coverage.sleepio.patient.download_sleepio_wellness_subtitle_1'; }
    if (platform === Platform.Web) { return 'coverage.sleepio.patient.download_sleepio_non_us_subtitle_desktop'; }
    return 'coverage.sleepio.patient.download_sleepio_non_us_subtitle_mobile';
  };
  const subtitle1 = isSpark
    ? t('coverage.spark.patient.download_app_subtitle_spark')
    : t(getSleepioSubtitleKey());

  if (platform === Platform.Android) {
    storeLink = (
      <StoreImgWrapper
        href={isSpark ? SPARK_GOOGLE_PLAY_URL : SLEEPIO_GOOGLE_PLAY_URL}
        onClick={() => {
          posthog.capture('DownloadAppClicked', {
            program: SLEEPIO_PROGRAM,
            os_platform: 'Android',
            'wellness.cortex_id': userUid,
          });
        }}
      >
        <img
          alt="Play Store"
          src={GooglePlay}
        />
      </StoreImgWrapper>
    );
  } else if (platform === Platform.iOS) {
    storeLink = (
      <StoreImgWrapper
        href={isSpark ? SPARK_APP_STORE_URL : SLEEPIO_APP_STORE_URL}
        onClick={() => {
          posthog.capture('DownloadAppClicked', {
            program: SLEEPIO_PROGRAM,
            os_platform: 'iOS',
            'wellness.cortex_id': userUid,
          });
        }}
      >
        <img
          alt="App Store"
          src={AppStore}
        />
      </StoreImgWrapper>
    );
  } else {
    qrCode = (
      <QRCodeContainer>
        <QRCodeWrapper>
          <QRCode
            data-testid="qr-code"
            value={`${window.location.origin}/${isSpark ? 'appstore/spark-direct' : 'appstore/sleepio'}`}
          />
        </QRCodeWrapper>
        <TextWrapper>
          {showSmsForm && (
            <Typography style={{ fontWeight: 'bold', paddingBottom: 16 }}>
              {t('coverage.sleepio.patient.download_sleepio_subtitle_2_desktop_header')}
            </Typography>
          )}
          <Typography>
            {t(showSmsForm
              ? 'coverage.sleepio.patient.download_sleepio_subtitle_2_desktop'
              : 'coverage.sleepio.patient.download_sleepio_non_us_qr_description')}
          </Typography>
        </TextWrapper>
      </QRCodeContainer>
    );
  }

  const canSubmit = isSpark ? (!!methods.getValues('phoneNumber') && !loading) : (isValid && !loading);

  const onSubmit: SubmitHandler<SetupForm> = async (data) => {
    setSubmitError('');
    const phoneForMutation = data.phoneNumber;
    const consentForMutation = isSpark ? new Date().toISOString() : textConsentAcknowledgedAt;

    try {
      const response = await sendReadyToDownloadSms({
        variables: {
          careTeamUid,
          phoneNumber: phoneForMutation,
          textConsentAcknowledgedAt: consentForMutation,
          marketingSmsConsent: data.marketingConsent || false,
        },
        context: {
          token: authToken,
        },
      });

      if (response.errors?.length > 0 || !response.data?.sendReadyToDownloadSms) {
        setPhoneNumberSubmitted(false);
        setSubmitError(t('errors.generic_support_error'));
        return;
      }

      setPhoneNumberSubmitted(true);
    } catch (error) {
      console.error(error);
      setPhoneNumberSubmitted(false);
      setSubmitError(t('errors.generic_support_error'));
    }
  };

  const displayPhone = methods.getValues('phoneNumber');

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ textAlign: 'center', width: '100%', paddingTop: 26 }}>
          <img
            alt={isSpark ? 'Spark Waving Limbot' : 'Sleepio Thumbsup'}
            style={{ width: '206px' }}
            src={isSpark ? LimbotHappy : EmoCloudSleep}
          />
        </Box>
        <Typography variant="h2" style={{ textAlign: 'center' }}>{title}</Typography>
        <Typography style={{ paddingBottom: 16, textAlign: 'center' }}>
          {subtitle1}
        </Typography>
        {/* Show store link above form for Sleepio, below for Spark. Always show for non-US Sleepio (no form). */}
        {(!showSmsForm || !isSpark) && storeLink}
        {showSmsForm && (
          phoneNumberSubmitted ? (
            <FormContainer style={{ textAlign: 'center' }}>
              <Typography style={{ paddingBottom: 16, fontWeight: 'bold' }}>
                {t('coverage.sleepio.patient.check_your_phone')}
              </Typography>
              <Typography style={{ paddingBottom: 16 }}>
                {t(isSpark
                  ? 'coverage.spark.patient.text_submitted_confirmation'
                  : 'coverage.sleepio.patient.text_submitted_confirmation')}
                {' '}
                <Typography component="span" style={{ fontWeight: 'bold' }}>
                  {displayPhone}
                </Typography>
              </Typography>
              <Typography>
                {t('coverage.sleepio.patient.didnt_receive_text')}
                &nbsp;
                <CustomLink
                  underline="always"
                  color="inherit"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isSpark) {
                      onSubmit(methods.getValues());
                    } else {
                      handleSubmit(onSubmit)();
                    }
                  }}
                >
                  {t('coverage.sleepio.patient.resend_the_message')}
                </CustomLink>
              </Typography>
            </FormContainer>
          ) : (
            <FormContainer>
              <Typography style={{ paddingBottom: 16 }}>
                {isSpark
                  ? t('coverage.spark.patient.submit_phone_number_spark')
                  : t('coverage.sleepio.patient.submit_phone_number')}
              </Typography>
              {!isSpark && (
                <>
                  <Controller
                    name="phoneNumber"
                    render={({ field }) => (
                      <PhoneNumberInput
                        placeholder={t('coverage.sleepio.patient.phone_label')}
                        value={field.value}
                        id="phoneNumber"
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={!!errors?.phoneNumber}
                        helperText={errors?.phoneNumber?.message}
                      />
                    )}
                  />
                  <Controller
                    name="agreeToReceiveTextMessages"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControlLabel
                        style={{ marginTop: 24, marginBottom: 16 }}
                        label={(
                          <Typography>
                            {t('coverage.sleepio.patient.agree_to_receive_text_messages')}
                          </Typography>
                        )}
                        control={(
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              if (e.target.checked) {
                                setTextConsentAcknowledgedAt(new Date().toISOString());
                              } else {
                                setTextConsentAcknowledgedAt('');
                              }
                            }}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="marketingConsent"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        style={{ marginTop: 8, marginBottom: 32 }}
                        label={(
                          <Typography>
                            {t('solera.account.marketing_consent')}
                          </Typography>
                        )}
                        control={(
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                          />
                        )}
                      />
                    )}
                  />
                </>
              )}
              <Button
                variant="contained"
                role="button"
                data-testid="submit-button"
                type="submit"
                fullWidth
                color="primary"
                disabled={!canSubmit}
                rounded
                loading={loading}
                onClick={(e) => {
                  if (isSpark) {
                    e.preventDefault();
                    onSubmit(methods.getValues());
                  } else {
                    handleSubmit(onSubmit)(e);
                  }
                }}
                style={
                  isSpark
                    ? {
                      borderRadius: 100,
                      ...(canSubmit
                        ? {
                          backgroundColor: sparkTheme.palette.primary.main,
                          color: sparkTheme.palette.getContrastText(
                            sparkTheme.palette.primary.main,
                          ),
                        }
                        : {}),
                    }
                    : undefined
                }
              >
                {isSpark ? t('coverage.send_sms') : t('coverage.submit')}
              </Button>
              {submitError && (
                <Typography color="error" style={{ marginTop: 8, textAlign: 'center' }}>
                  {submitError}
                </Typography>
              )}
            </FormContainer>
          )
        )}
        {showSmsForm && isSpark && storeLink}
        {qrCode}
      </Box>
    </FormProvider>
  );
};
